import React, { useMemo, useState } from "react";
import { geoEqualEarth, geoPath, geoGraticule10, geoInterpolate } from "d3-geo";
import { feature } from "topojson-client";
import worldTopo from "world-atlas/countries-110m.json";
import { useTheme } from "../lib/theme";

const W = 1000;
const H = 470;

export const verdictColors = (viz) => ({
  ALLOWED: viz.emerald[0],
  STEP_UP: viz.amber[0],
  BLOCKED: viz.orange,
  IMPOSSIBLE_TRAVEL: viz.red[0],
});

const land = feature(worldTopo, worldTopo.objects.countries);
const projection = geoEqualEarth().fitExtent(
  [
    [14, 12],
    [W - 14, H - 12],
  ],
  { type: "Sphere" }
);
const pathGen = geoPath(projection);
const spherePath = pathGen({ type: "Sphere" });
const graticulePath = pathGen(geoGraticule10());
const countryPaths = land.features.map((f) => ({ id: f.id, d: pathGen(f) }));

const arcPath = (from, to) => {
  const interp = geoInterpolate(from, to);
  const pts = Array.from({ length: 64 }, (_, i) => interp(i / 63));
  const segments = [[pts[0]]];
  for (let i = 1; i < pts.length; i++) {
    const jump = Math.abs(pts[i][0] - pts[i - 1][0]) > 170;
    if (jump) segments.push([]);
    segments[segments.length - 1].push(pts[i]);
  }
  const coords = segments.filter((s) => s.length > 1);
  if (!coords.length) return null;
  return pathGen({ type: "MultiLineString", coordinates: coords });
};

export function ThreatMap({ events = [] }) {
  const [hover, setHover] = useState(null);
  const { viz } = useTheme();
  const VERDICT = useMemo(() => verdictColors(viz), [viz]);

  const points = useMemo(
    () =>
      events
        .map((e) => {
          const xy = projection([e.lon, e.lat]);
          if (!xy) return null;
          return { ...e, x: xy[0], y: xy[1] };
        })
        .filter(Boolean),
    [events]
  );

  const arcs = useMemo(
    () =>
      events
        .filter((e) => e.verdict === "IMPOSSIBLE_TRAVEL" && e.prev_lat !== null && e.prev_lon !== null)
        .slice(0, 6)
        .map((e, i) => ({
          key: `${e.event_id}-${i}`,
          d: arcPath([e.prev_lon, e.prev_lat], [e.lon, e.lat]),
          label: `${e.prev_city} → ${e.city}`,
          delay: i * 0.45,
        }))
        .filter((a) => a.d),
    [events]
  );

  return (
    <div className="relative overflow-hidden rounded-lg border border-edge well">
      <svg viewBox={`0 0 ${W} ${H}`} className="block w-full" data-testid="threat-map-svg">
        <defs>
          <radialGradient id="tm-ocean" cx="50%" cy="42%" r="72%">
            <stop offset="0%" stopColor={viz.ocean[0]} />
            <stop offset="100%" stopColor={viz.ocean[1]} />
          </radialGradient>
          <linearGradient id="tm-arc" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={viz.arcFrom} />
            <stop offset="45%" stopColor={viz.arcMid} />
            <stop offset="100%" stopColor={viz.arcTo} />
          </linearGradient>
          <filter id="tm-glow" x="-200%" y="-200%" width="500%" height="500%">
            <feGaussianBlur stdDeviation="1.5" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <path d={spherePath} fill="url(#tm-ocean)" stroke={viz.sphere} strokeWidth="0.8" />
        <path d={graticulePath} fill="none" stroke={viz.graticule} strokeWidth="0.5" />
        {countryPaths.map((c, i) => (
          <path key={`${c.id}-${i}`} d={c.d} fill={viz.landFill} stroke={viz.landStroke} strokeWidth="0.45" />
        ))}

        {arcs.map((a) => (
          <g key={a.key}>
            <path d={a.d} fill="none" stroke={viz.arcHalo} strokeWidth="2.6" />
            <path
              d={a.d}
              fill="none"
              stroke="url(#tm-arc)"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeDasharray="150 900"
              filter="url(#tm-glow)"
            >
              <animate attributeName="stroke-dashoffset" from="1050" to="0" dur="2.6s" begin={`${a.delay}s`} repeatCount="indefinite" />
            </path>
          </g>
        ))}

        {points.map((p, i) => {
          const color = VERDICT[p.verdict] || viz.cyan[0];
          const fresh = i < 8;
          const r = p.verdict === "IMPOSSIBLE_TRAVEL" ? 4.4 : 3.1;
          return (
            <g
              key={p.event_id}
              onMouseEnter={() => setHover(p)}
              onMouseLeave={() => setHover(null)}
              style={{ cursor: "pointer" }}
            >
              {fresh && (
                <circle cx={p.x} cy={p.y} r={r} fill="none" stroke={color} strokeWidth="1.1" opacity="0.85">
                  <animate attributeName="r" from={r} to={r * 6} dur="2.4s" begin={`${i * 0.22}s`} repeatCount="indefinite" />
                  <animate attributeName="opacity" from="0.7" to="0" dur="2.4s" begin={`${i * 0.22}s`} repeatCount="indefinite" />
                </circle>
              )}
              <circle cx={p.x} cy={p.y} r={r + 3.4} fill={color} opacity="0.16" />
              <circle cx={p.x} cy={p.y} r={r} fill={color} filter="url(#tm-glow)" />
              <circle cx={p.x} cy={p.y} r={r + 6} fill="transparent" />
            </g>
          );
        })}
      </svg>

      {hover && (
        <div
          className="pointer-events-none absolute z-10 max-w-[260px] rounded-md border border-edgeStrong bg-void/95 px-3 py-2"
          style={{ left: `${(hover.x / W) * 100}%`, top: `${(hover.y / H) * 100}%`, transform: "translate(12px, -110%)" }}
          data-testid="threat-map-tooltip"
        >
          <p className="font-mono text-[10px]" style={{ color: VERDICT[hover.verdict] }}>
            {hover.verdict}
          </p>
          <p className="text-[12px] text-slate-100">
            {hover.city}, {hover.country}
          </p>
          <p className="font-mono text-[10px] text-slate-400">{hover.identity}</p>
          <p className="font-mono text-[10px] text-slate-400">
            {hover.ip} · {hover.asn}
          </p>
          {hover.verdict === "IMPOSSIBLE_TRAVEL" && (
            <p className="mt-1 font-mono text-[10px] text-aegis-red">
              {hover.prev_city} → {hover.city} · {hover.distance_km} km / {hover.delta_minutes} min ≈ {hover.speed_kmh} km/h
            </p>
          )}
        </div>
      )}
    </div>
  );
}
